// src/utils/publiekOverzicht.js
// De publieke voorvertoningen per soort (doorloopje, waarom- en hoe-stuk): waar ze op de site staan,
// waar de backend ze geeft en waar ze in de app staan. En het overzicht (/alle-doorloopjes) in
// groepen: de doorloopjes per thema (op titel, die zonder thema als laatste onder "Overig"), daarna
// de waarom- en de hoe-stukken; elke groep met zijn soort en een anker om naartoe te springen. Op de
// pagina staan per groep eerst de uitgelichte, dan die met een afbeelding (vooraan). En per voorvertoning
// zijn buren (buren), waarnaar hij linkt, en de datum waarop hij het laatst veranderde (laatstGewijzigd).
// CommonJS, zoals seoText.js: de site en de scripts (prerender, sitemap, llms.txt) gebruiken het allemaal.
// Zonder object-spread: Babel zet daarvoor een ESM-helper in en dan ziet webpack de exports niet.
const SOORTEN = {
  doorloopje: { pad: '/doorloopje', api: '/api/doorloopjes/preview', app: '/doorloopjes', naam: 'Doorloopje', meervoud: 'Doorloopjes' },
  why: { pad: '/waarom', api: '/api/whies/preview', app: '/why', naam: 'Waarom', meervoud: 'Waaroms' },
  how: { pad: '/hoe', api: '/api/hows/preview', app: '/how', naam: 'Hoe', meervoud: 'Hoes' },
};

const OVERZICHT_PAD = '/alle-doorloopjes';

const voorvertoningPad = (slug, soort = 'doorloopje') => `${SOORTEN[soort].pad}/${slug}`;

function perThema(doorloopjes) {
  const groepen = new Map();
  for (const d of doorloopjes) {
    const naam = (d.theme && d.theme.title) || 'Overig';
    if (!groepen.has(naam)) groepen.set(naam, []);
    groepen.get(naam).push(d);
  }
  return [...groepen.entries()]
    .sort(([a], [b]) => (a === 'Overig') - (b === 'Overig') || a.localeCompare(b, 'nl'))
    .map(([titel, items]) => ({ titel, soort: 'doorloopje', items }));
}

const anker = (titel) =>
  titel.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function overzichtsGroepen(items) {
  const vanSoort = (soort) => items.filter((d) => d.type === soort);
  return [
    ...perThema(vanSoort('doorloopje')),
    { titel: SOORTEN.why.naam, soort: 'why', items: vanSoort('why') },
    { titel: SOORTEN.how.naam, soort: 'how', items: vanSoort('how') },
  ]
    .filter((groep) => groep.items.length > 0)
    .map((groep) => Object.assign(groep, { anker: anker(groep.titel) }));
}

// Uitgelicht eerst, dan met een afbeelding, verder in de volgorde van de lijst (op titel).
const rang = (d) => (d.featured ? 0 : d.image ? 1 : 2);
const vooraan = (items) => items.slice().sort((a, b) => rang(a) - rang(b));

// Hoeveel anderen uit zijn groep een voorvertoning toont, naast de vorige en de volgende.
const VERWANT = 6;

// De buren van een voorvertoning, in de volgorde van het overzicht: de vorige en de volgende in de hele
// lijst (de laatste van een groep leidt zo naar de eerste van de volgende groep, en de laatste van de
// lijst naar de eerste) en tot VERWANT anderen uit zijn groep, om en om erna en ervoor, rondom. Zo linkt
// elke voorvertoning naar andere en linken andere naar hem, en vormt elke groep één kring: wie (of welke
// zoekmachine) op één pagina binnenkomt, komt via de links bij alle. null als het item niet in de lijst staat.
function buren(items, soort, slug) {
  const groepen = overzichtsGroepen(items);
  const volgorde = [].concat(...groepen.map((g) => g.items));
  const i = volgorde.findIndex((d) => d.type === soort && d.slug === slug);
  if (i < 0) return null;
  const zelf = volgorde[i];
  const groep = groepen.find((g) => g.items.includes(zelf));
  const n = volgorde.length;
  const vorige = n > 1 ? volgorde[(i - 1 + n) % n] : null;
  const volgende = n > 1 ? volgorde[(i + 1) % n] : null;
  const j = groep.items.indexOf(zelf);
  const m = groep.items.length;
  const verwant = [];
  for (let k = 1; k < m && verwant.length < VERWANT; k++) {
    for (const d of [groep.items[(j + k) % m], groep.items[(j - k + m) % m]]) {
      if (verwant.length < VERWANT && d !== zelf && d !== vorige && d !== volgende && !verwant.includes(d)) verwant.push(d);
    }
  }
  return { groep: { titel: groep.titel, soort: groep.soort, anker: groep.anker }, vorige, volgende, verwant };
}

// De kop boven de buren uit zijn groep.
function meerTitel({ titel, soort }) {
  if (soort !== 'doorloopje') return `Meer ${SOORTEN[soort].meervoud}`;
  if (titel === 'Overig') return 'Meer doorloopjes';
  return `Meer doorloopjes over ${titel}`;
}

// De laatste keer dat de HTML van alle voorvertoningen veranderde buiten hun eigen inhoud om (de opmaak, de
// links naar de buren). Hoog hem op in dezelfde wijziging die dat verandert.
const VOORVERTONINGEN_GEWIJZIGD = '2026-11-04';

const dag = (tijd) => (tijd ? String(tijd).split('T')[0] : '');

// Wanneer de pagina van een voorvertoning het laatst veranderde (JJJJ-MM-DD): het item zelf, een buur
// waarvan hij titel en eerste regel toont, of de opmaak van alle voorvertoningen. Voor de sitemap.
function laatstGewijzigd(items, d) {
  const b = buren(items, d.type, d.slug);
  const getoond = b ? [d, b.vorige, b.volgende, ...b.verwant].filter(Boolean) : [d];
  return getoond.map((x) => dag(x.updatedAt)).concat(VOORVERTONINGEN_GEWIJZIGD).sort().pop();
}

module.exports = { SOORTEN, OVERZICHT_PAD, voorvertoningPad, overzichtsGroepen, vooraan, buren, meerTitel, laatstGewijzigd };
